import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ArrowLeft, AlertCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { API, SITE } from "@/lib/site";

const COPY = {
    de: {
        polling: "Wir bestätigen deine Zahlung...",
        success_title: "Buchung bestätigt.",
        success_lead:
            "Vielen Dank für dein Vertrauen. Mercedes meldet sich persönlich innerhalb von 24 Stunden.",
        expired: "Die Zahlungs-Sitzung ist abgelaufen. Bitte versuche es erneut.",
        unknown: "Wir konnten den Status deiner Zahlung nicht bestätigen. Bitte schreibe uns kurz.",
        back: "Zurück zur Startseite",
        receipt: "Buchungsdetails",
        product: "Produkt",
        amount: "Betrag",
        confirmation_to: "Eine Bestätigung wurde an deine E-Mail gesendet.",
    },
    es: {
        polling: "Confirmando tu pago...",
        success_title: "Compra confirmada.",
        success_lead:
            "Gracias por tu confianza. Mercedes te contactará personalmente en menos de 24 horas.",
        expired: "La sesión de pago ha caducado. Inténtalo de nuevo.",
        unknown: "No pudimos confirmar el estado de tu pago. Escríbenos por favor.",
        back: "Volver al inicio",
        receipt: "Detalles de compra",
        product: "Producto",
        amount: "Importe",
        confirmation_to: "Se ha enviado una confirmación a tu correo.",
    },
};

const POLL_INTERVAL_MS = 2200;
const MAX_ATTEMPTS = 8;

const PaymentSuccess = () => {
    const { lang } = useLanguage();
    const T = COPY[lang] || COPY.de;
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ stage: "polling", data: null });
    const attemptsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ stage: "unknown", data: null });
            return;
        }
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            attemptsRef.current += 1;
            try {
                const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setState({ stage: "success", data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ stage: "expired", data });
                    return;
                }
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    setState({ stage: "unknown", data });
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            } catch (e) {
                if (cancelled) return;
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    setState({ stage: "unknown", data: null });
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <div data-testid="payment-success-page" className="min-h-screen bg-ella-cream flex items-center justify-center px-6">
            <div className="max-w-xl w-full text-center py-20">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ella-muted hover:text-ella-ink transition-colors"
                    data-testid="payment-back-home"
                >
                    <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {T.back}
                </Link>

                <img
                    src={SITE.logoUrl}
                    alt="ELLA"
                    className="h-16 w-auto mx-auto mt-10 rounded-md object-contain"
                />

                {state.stage === "polling" && (
                    <div className="mt-12" data-testid="payment-state-polling">
                        <Loader2 className="w-10 h-10 mx-auto text-ella-gold animate-spin" strokeWidth={1.5} />
                        <p className="mt-6 font-display text-2xl text-ella-ink">{T.polling}</p>
                    </div>
                )}

                {state.stage === "success" && (
                    <div className="mt-12" data-testid="payment-state-success">
                        <div className="w-20 h-20 mx-auto rounded-full bg-ella-ink flex items-center justify-center text-ella-gold">
                            <Check className="w-9 h-9" strokeWidth={1.5} />
                        </div>
                        <h1 className="ella-headline text-4xl md:text-5xl mt-8 text-ella-ink">
                            {T.success_title}
                        </h1>
                        <p className="mt-5 text-ella-ink/70 font-light leading-relaxed">
                            {T.success_lead}
                        </p>

                        {state.data && (
                            <div
                                className="mt-10 mx-auto max-w-sm rounded-2xl border border-ella-line bg-white p-6 text-left"
                                data-testid="payment-receipt"
                            >
                                <p className="ella-overline">{T.receipt}</p>
                                <div className="mt-3 flex justify-between text-sm">
                                    <span className="text-ella-muted">{T.product}</span>
                                    <span className="text-ella-ink font-medium">
                                        {state.data.product_name}
                                    </span>
                                </div>
                                <div className="mt-2 flex justify-between text-sm">
                                    <span className="text-ella-muted">{T.amount}</span>
                                    <span className="text-ella-ink font-medium">
                                        {state.data.amount?.toFixed(2)} {state.data.currency}
                                    </span>
                                </div>
                            </div>
                        )}

                        <p className="mt-8 text-xs text-ella-muted italic">{T.confirmation_to}</p>
                    </div>
                )}

                {state.stage === "expired" && (
                    <div className="mt-12" data-testid="payment-state-expired">
                        <AlertCircle className="w-12 h-12 mx-auto text-ella-gold-dark" strokeWidth={1.5} />
                        <p className="mt-6 font-display text-2xl text-ella-ink">{T.expired}</p>
                    </div>
                )}

                {state.stage === "unknown" && (
                    <div className="mt-12" data-testid="payment-state-unknown">
                        <AlertCircle className="w-12 h-12 mx-auto text-ella-muted" strokeWidth={1.5} />
                        <p className="mt-6 font-display text-2xl text-ella-ink">{T.unknown}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccess;
